import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Switch,
} from '@mui/material';
import { HeadCell } from '../../../common/table/EnhancedTableHead';
import { EnhancedTable } from '../../../common/table/EnhancedTable';
import React, { useMemo, useState } from 'react';
import { ApplicationModel, ExpiredDealState } from '../../../generated/ApiClient';
import { DateTimeFormatter, dateFormat } from '../../../common/formatter/DateTimeFormatter';
import { usePermissions } from '../../../common/auth/PermissionsContext';
import { DashboardContractExpiredIndicator } from './DashboardContractExpiredIndicator';
import { DashboardApplicationOverlay } from '../applications/DashboardApplicationOverlay';
import { dashboardContentGrid, dashboardSideGrid } from '../Dashboard';
import { useExpiredDealsQuery } from './useExpiredDealsQuery';

interface DashboardRow {
  id: string;
  contractNumber: string;
  salesPerson: string;
  customer: string;
  modelName: string;
  type: string;
  creationDate: string;
  expiryDate?: string;
  status: JSX.Element;
  endDate?: string;
  activationDate?: string;
}

const mapApplicationModelToRow = (
  am: ApplicationModel,
  currentState: ExpiredDealState
): DashboardRow => ({
  id: am.contractId.toString(),
  contractNumber: am.contractId?.toString() ?? '',
  customer: am.customerName ?? '',
  modelName: am.objectName ?? '',
  type: am.financingTypeDisplayName ?? '',
  expiryDate: DateTimeFormatter.format(am.expirationDate, { format: dateFormat }),
  creationDate: DateTimeFormatter.format(am.creationDate, { format: dateFormat }),
  salesPerson: am.salesPerson ?? '',
  status: <DashboardContractExpiredIndicator state={currentState} />,
  activationDate: DateTimeFormatter.format(am.activationDate, { format: dateFormat }),
  endDate: DateTimeFormatter.format(am.endDate, { format: dateFormat }),
});

export function DashboardContractExpired() {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const [openDoc, setOpenDoc] = useState<string | undefined>();

  const [selState, setSelState] = useState<ExpiredDealState>(ExpiredDealState.LessThan14Days);
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, isFetching } = useExpiredDealsQuery(selState, showAll);

  const rows = useMemo(
    () => (data ?? []).map((x) => mapApplicationModelToRow(x, selState)),
    [data, selState]
  );

  const headCells: readonly HeadCell<DashboardRow>[] = [
    {
      id: 'status',
      disablePadding: true,
      label: '',
      width: '45px;',
    },
    {
      id: 'contractNumber',
      disablePadding: false,
      label: t('dashboard.applications.contractNumber'),
      allowSorting: true,
      columnName: 'contractNumber',
      allowFiltering: true,
      width: '220px',
    },
    {
      id: 'salesPerson',
      disablePadding: false,
      label: t('dashboard.applications.salesPerson'),
      allowSorting: true,
      columnName: 'salesPerson',
      allowFiltering: true,
      width: '280px',
    },
    {
      id: 'customer',
      disablePadding: false,
      label: t('dashboard.applications.customer'),
      allowSorting: true,
      columnName: 'customer',
      allowFiltering: true,
      width: '280px',
    },
    {
      id: 'modelName',
      disablePadding: false,
      label: t('dashboard.applications.modelName'),
      allowSorting: true,
      columnName: 'modelName',
      allowFiltering: true,
    },
    {
      id: 'type',
      disablePadding: false,
      label: t('dashboard.applications.type'),
      allowSorting: true,
      columnName: 'type',
      allowFiltering: true,
      width: '280px',
    },
    {
      id: 'creationDate',
      disablePadding: false,
      label: t('dashboard.applications.creationDate'),
      allowSorting: true,
      columnName: 'creationDate',
      allowFiltering: true,
      width: '180px',
      sortTransform: (value) => DateTimeFormatter.parse(value, { format: dateFormat }),
    },
    {
      id: 'expiryDate',
      disablePadding: false,
      label: t('dashboard.applications.expiryDate'),
      allowSorting: true,
      columnName: 'expiryDate',
      allowFiltering: true,
      width: '180px',
      sortTransform: (value) => DateTimeFormatter.parse(value, { format: dateFormat }),
    },
    {
      id: 'activationDate',
      disablePadding: false,
      label: t('dashboard.applications.activationDate'),
      allowSorting: true,
      columnName: 'activationDate',
      allowFiltering: true,
      width: '180px',
      sortTransform: (value) => DateTimeFormatter.parse(value, { format: dateFormat }),
    },
    {
      id: 'endDate',
      disablePadding: false,
      label: t('dashboard.applications.endDate'),
      allowSorting: true,
      columnName: 'endDate',
      allowFiltering: true,
      width: '180px',
      sortTransform: (value) => DateTimeFormatter.parse(value, { format: dateFormat }),
    },
  ];

  const getButtonProps = (state: ExpiredDealState) => ({
    selected: selState === state,
    onClick: () => setSelState(state),
    disabled: isLoading,
  });

  const handleRowClick = (e: React.MouseEvent<unknown>, id: string) => {
    e.stopPropagation();
    setOpenDoc(id);
  };

  return (
    <Grid container className="dashboard-table">
      <DashboardApplicationOverlay
        isDrawerOpen={Boolean(openDoc)}
        onClose={() => setOpenDoc(undefined)}
        cmsId={openDoc}
        showExtend
      />
      <Grid item {...dashboardSideGrid} height="100%">
        <Box className="dashboard-table-sidebar">
          <List>
            {[ExpiredDealState.LessThan14Days, ExpiredDealState.LessThan25Days].map((i) => (
              <ListItemButton {...getButtonProps(i)}>
                <ListItemText
                  primary={
                    // @ts-ignore
                    t(`dashboard.expired.states.${i}`, '')
                  }
                  sx={{ mr: 2 }}
                />
                <DashboardContractExpiredIndicator state={i} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Grid>
      <Grid item {...dashboardContentGrid}>
        {!hasPermission('Dashboard.AllUsersContracts', 'View') &&
          hasPermission('Dashboard.EmployeeContracts', 'View') && (
            <Box sx={{ ml: 1 }}>
              <FormControlLabel
                onChange={() => setShowAll(!showAll)}
                control={<Switch checked={showAll} />}
                label={t('dashboard.applications.showAll')}
              />
            </Box>
          )}
        <EnhancedTable
          rows={rows}
          total={rows.length}
          loading={isLoading}
          isFetching={isFetching}
          headCells={headCells}
          hideCheckbox={true}
          variant="virtual"
          onRowClick={handleRowClick}
          defaultOrderBy="contractNumber"
          defaultOrder="desc"
          enableXlsxExport={true}
        />
      </Grid>
    </Grid>
  );
}
